/* eslint-disable react-refresh/only-export-components -- provider and its route-scoped hooks share one private context. */
import { App as AntdApp } from 'antd';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

const DEFAULT_MESSAGE = '当前页面有未保存的修改，放弃后将无法恢复。';

interface UnsavedChangesContextValue {
  readonly confirmDiscard: (message?: string) => Promise<boolean>;
  readonly register: (id: string, dirty: boolean) => void;
  readonly unregister: (id: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | undefined>(undefined);

export function UnsavedChangesProvider({ children }: { readonly children: ReactNode }) {
  const { modal } = AntdApp.useApp();
  const sources = useRef<Record<string, boolean>>({});
  const hasDirtySource = useCallback(() => Object.values(sources.current).some(Boolean), []);
  const blocker = useBlocker(hasDirtySource);

  const confirmDiscard = useCallback(
    (message = DEFAULT_MESSAGE) =>
      new Promise<boolean>((resolve) => {
        modal.confirm({
          cancelText: '继续编辑',
          centered: true,
          closable: false,
          content: message,
          keyboard: false,
          mask: { closable: false },
          okButtonProps: { danger: true },
          okText: '放弃修改',
          onCancel: () => {
            resolve(false);
          },
          onOk: () => {
            resolve(true);
          },
          title: '放弃未保存的修改？',
        });
      }),
    [modal],
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let active = true;
    void confirmDiscard().then((discard) => {
      if (!active) return;
      if (discard) blocker.proceed();
      else blocker.reset();
    });
    return () => {
      active = false;
    };
  }, [blocker, confirmDiscard]);

  useBeforeUnload(
    (event) => {
      if (!hasDirtySource()) return;
      event.preventDefault();
    },
    { capture: true },
  );

  const register = useCallback((id: string, dirty: boolean) => {
    if (sources.current[id] === dirty) return;
    sources.current = { ...sources.current, [id]: dirty };
  }, []);
  const unregister = useCallback((id: string) => {
    if (!(id in sources.current)) return;
    sources.current = Object.fromEntries(
      Object.entries(sources.current).filter(([sourceId]) => sourceId !== id),
    );
  }, []);
  const contextValue = useMemo(
    () => ({ confirmDiscard, register, unregister }),
    [confirmDiscard, register, unregister],
  );

  return (
    <UnsavedChangesContext.Provider value={contextValue}>{children}</UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesSource(dirty: boolean) {
  const context = useUnsavedChangesContext();
  const id = useId();
  const setSourceDirty = useCallback(
    (nextDirty: boolean) => {
      context.register(id, nextDirty);
    },
    [context, id],
  );
  useEffect(() => {
    setSourceDirty(dirty);
    return () => {
      context.unregister(id);
    };
  }, [context, dirty, id, setSourceDirty]);
  return setSourceDirty;
}

export function useDirtyForm() {
  const [dirty, setDirty] = useState(false);
  const setSourceDirty = useUnsavedChangesSource(dirty);
  const updateDirty = (nextDirty: boolean) => {
    setSourceDirty(nextDirty);
    setDirty(nextDirty);
  };
  return {
    clearDirty: () => {
      updateDirty(false);
    },
    dirty,
    formProps: {
      onValuesChange: () => {
        updateDirty(true);
      },
    },
    markDirty: () => {
      updateDirty(true);
    },
  } as const;
}

interface GuardedCloseOptions {
  readonly dirty: boolean;
  readonly message?: string;
  readonly onDiscard: () => void;
}

export function useGuardedClose({ dirty, message, onDiscard }: GuardedCloseOptions) {
  const { confirmDiscard } = useUnsavedChangesContext();
  return () => {
    if (!dirty) {
      onDiscard();
      return;
    }
    void confirmDiscard(message).then((discard) => {
      if (discard) onDiscard();
    });
  };
}

function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (context === undefined) {
    throw new Error('Unsaved changes hooks must be used within UnsavedChangesProvider');
  }
  return context;
}
